import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MARGIN = 8;
const GAP = 8;

interface Props {
  /** The element the tip points at; nothing renders while it's null. */
  anchor: HTMLElement | null;
  children: React.ReactNode;
  className?: string;
}

/**
 * A hover tip drawn on top of everything (portaled to <body>), kept inside the screen:
 * above the anchor when there's room, otherwise below, and slid sideways near the edges.
 */
const FloatingTip: React.FC<Props> = ({ anchor, children, className }) => {
  const tipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!anchor || !tipRef.current) {
      setPos(null);
      return;
    }
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const t = tipRef.current!.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const left = Math.min(Math.max(a.left + a.width / 2 - t.width / 2, MARGIN), vw - t.width - MARGIN);
      const above = a.top - GAP - t.height;
      const top = above >= MARGIN ? above : Math.min(a.bottom + GAP, vh - t.height - MARGIN);
      setPos({ left, top });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchor, children]);

  if (!anchor) return null;
  return createPortal(
    <div
      ref={tipRef}
      role="tooltip"
      className={`pointer-events-none fixed z-[10000] ${className ?? ''}`}
      style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
    >
      {children}
    </div>,
    document.body,
  );
};

export default FloatingTip;
